import { useMemo } from 'react';
import type {} from '@react-three/fiber';
import * as THREE from 'three';
import { indexToSquare, squareToIndex, type ShieldMap } from '@strategy4kids/game-engine';
import { squareToVec3 } from './scene.js';

export type HighlightKind =
  | 'selected'
  | 'movable'
  | 'path'
  | 'target-valid'
  | 'target-capture'
  | 'last-from'
  | 'last-to'
  | 'check'
  | 'ply-from'
  | 'ply-to'
  | 'shield-defended'
  | 'shield-threatened'
  | 'shield-source';

export interface Highlight {
  readonly square: string;
  readonly kind: HighlightKind;
  /** Index into the look-ahead chain. Undefined for non-ply highlights. */
  readonly plyIndex?: number;
}

export interface HighlightInput {
  readonly selected: string | null;
  /** Squares of the player's pieces that have a legal move (assistance option). */
  readonly movable?: ReadonlyArray<string> | null;
  /** Squares the selected piece passes over (assistance option). */
  readonly paths?: ReadonlyArray<string> | null;
  readonly targets: ReadonlyArray<{ square: string; capture: boolean }>;
  readonly lastMove: { from: string; to: string } | null;
  readonly checkSquare: string | null;
  /** Engine PV after the player's intended move. Even-index plies are
   *  opponent moves (cyan rings); odd-index plies are the player's
   *  counter-replies (mint rings). The chain is capped by the
   *  theme-store depth setting. */
  readonly predictedReply?: ReadonlyArray<{ from: string; to: string }> | null;
  /** Defence/attack summary for the pieces the viewer owns. Build it with
   *  `buildShieldMap`, which is already scoped to one colour, so this never
   *  carries the opponent's own defenders — rendering those would put ~20
   *  extra rings on the board and destroy the signal. */
  readonly shields?: ShieldMap | null;
  /** Square whose shield / threat detail is expanded. Drives the source dots
   *  that name *which* piece is doing the shielding. */
  readonly reveal?: string | null;
  /** Map used to resolve `reveal`. Defaults to `shields`. */
  readonly revealShields?: ShieldMap | null;
}

/**
 * Last-move tile opacity as a function of whether the predicted-reply hint
 * is on screen. Pulled out as a pure helper so we can unit-test the
 * dimming rule without spinning up a R3F renderer.
 *
 * Rule: when the predicted hint is active (`predictedReply != null`), dim
 * the amber last-move tiles to 30% of their baseline opacity so the cyan
 * predicted ring reads as the primary signal. When the hint is gone
 * (`predictedReply == null` or `undefined`), the amber tiles return to
 * full opacity — history is the only signal, so it stays legible.
 */
export function lastMoveOpacityFor(
  predictedReply: ReadonlyArray<{ from: string; to: string }> | null | undefined,
): number {
  const baseline = KINDS['last-to'].opacity;
  return predictedReply == null ? baseline : baseline * 0.3;
}

/**
 * The always-on shield markers, plus the reveal dots for `reveal`.
 *
 * Pulled out as a pure helper for the same reason as `lastMoveOpacityFor` —
 * the relevance rule ("which squares get a marker at all") is the part worth
 * testing, and testing it should not require standing up an R3F renderer.
 *
 * `shields` is expected to be built for ONE colour (the viewer's), which
 * `buildShieldMap` already guarantees. That scoping is what keeps the board
 * readable: the opponent's pieces have defenders too, and drawing those would
 * add ~20 rings that a kid cannot act on.
 */
export function markersFor(
  shields: ShieldMap | null,
  reveal: string | null,
  revealShields: ShieldMap | null = null,
): Highlight[] {
  if (!shields) return [];
  const out: Highlight[] = [];
  for (const index of shields.defended.keys()) {
    out.push({ square: indexToSquare(index), kind: 'shield-defended' });
  }
  for (const index of shields.threatened.keys()) {
    out.push({ square: indexToSquare(index), kind: 'shield-threatened' });
  }
  // The reveal dots name the actual defenders and attackers, which is the
  // "clicking a shield shows which piece is shielding" requirement. Friendly
  // defenders first, then threats, so the ordering is stable for assertions.
  //
  // Resolved against `revealShields`, not `shields`: the always-on markers
  // are scoped to the viewer, but the reveal may be on an enemy piece, whose
  // defenders the viewer's map never recorded.
  const detail = revealShields ?? shields;
  if (reveal) {
    const index = squareToIndex(reveal);
    for (const src of detail.defended.get(index) ?? []) {
      out.push({ square: indexToSquare(src), kind: 'shield-source' });
    }
    for (const src of detail.threatened.get(index) ?? []) {
      out.push({ square: indexToSquare(src), kind: 'shield-source' });
    }
  }
  return out;
}

export function computeHighlights(input: HighlightInput): Highlight[] {
  const out: Highlight[] = [];
  for (const sq of input.movable ?? []) {
    if (sq !== input.selected) out.push({ square: sq, kind: 'movable' });
  }
  for (const sq of input.paths ?? []) out.push({ square: sq, kind: 'path' });
  if (input.selected) out.push({ square: input.selected, kind: 'selected' });
  for (const t of input.targets) {
    out.push({ square: t.square, kind: t.capture ? 'target-capture' : 'target-valid' });
  }
  if (input.lastMove) {
    out.push({ square: input.lastMove.from, kind: 'last-from' });
    out.push({ square: input.lastMove.to, kind: 'last-to' });
  }
  if (input.checkSquare) out.push({ square: input.checkSquare, kind: 'check' });
  // Look-ahead chain: cyan (opponent) rings on even plies, mint (player)
  // rings on odd plies. Distinct from amber (last-move) and red (check) so
  // the player reads it as "engine would probably continue here".
  if (input.predictedReply) {
    input.predictedReply.forEach((ply, i) => {
      out.push({ square: ply.from, kind: 'ply-from', plyIndex: i });
      out.push({ square: ply.to, kind: 'ply-to', plyIndex: i });
    });
  }
  out.push(...markersFor(input.shields ?? null, input.reveal ?? null, input.revealShields ?? null));
  return out;
}

export interface SquareTarget {
  readonly square: string;
  readonly capture: boolean;
}

const ringGeometry = new THREE.RingGeometry(0.22, 0.34, 24);

/**
 * Shields get their own geometry, not just their own colour.
 *
 * By the time a third overlay lands, the chromatic channels on this board are
 * nearly exhausted — yellow (selected), green (target), red (capture/check),
 * amber (last move), cyan + mint (ply chain). Hue alone can no longer
 * separate "this piece is defended" from "you can move here", so shape
 * carries the meaning and colour reinforces it:
 *
 *   defended  — a wide outline ring, sitting OUTSIDE the move ring so the two
 *               never overlap on a square that is both a target and shielded
 *   threatened — a 4-segment ring, which renders as a diamond. Reads as "this
 *               piece is in the open" with no legend required.
 *   source    — a small FILLED disc. Filled-vs-outline is the strongest
 *               available channel, and a small centre dot leaves the piece art
 *               legible.
 */
const shieldRingGeometry = new THREE.RingGeometry(0.30, 0.38, 24);
const threatDiamondGeometry = new THREE.RingGeometry(0.36, 0.45, 4);
const sourceDiscGeometry = new THREE.CircleGeometry(0.13, 16);
// The move-path dot is the only marker that has to read on a LIGHT square at a
// glance, so it is larger than the shield source dot (0.13) and darker below.
const pathDotGeometry = new THREE.CircleGeometry(0.19, 20);

const SHIELD_GEOMETRY: Partial<Record<HighlightKind, THREE.BufferGeometry>> = {
  'shield-defended': shieldRingGeometry,
  'shield-threatened': threatDiamondGeometry,
  'shield-source': sourceDiscGeometry,
  path: pathDotGeometry,
};

function geometryFor(kind: HighlightKind): THREE.BufferGeometry {
  return SHIELD_GEOMETRY[kind] ?? ringGeometry;
}

const KINDS: Record<HighlightKind, { color: string; opacity: number; y: number }> = {
  selected: { color: '#ffd54a', opacity: 0.55, y: 0.03 },
  // Soft sky-blue under everything else: "this piece can move", not "go here".
  path: { color: '#2fbf71', opacity: 0.85, y: 0.027 },
  movable: { color: '#5ec8ff', opacity: 0.6, y: 0.026 },
  'target-valid': { color: '#44ff88', opacity: 0.5, y: 0.035 },
  'target-capture': { color: '#ff5555', opacity: 0.55, y: 0.035 },
  'last-from': { color: '#ffb84d', opacity: 0.25, y: 0.028 },
  'last-to': { color: '#ffb84d', opacity: 0.25, y: 0.028 },
  check: { color: '#ff2d2d', opacity: 0.6, y: 0.04 },
  // Ply rings: actual color is decided per-ply in the render step (cyan
  // for opponent, mint for player). These base entries are fallbacks for
  // any caller that pushes a 'ply-from' / 'ply-to' without a plyIndex;
  // the live path always sets plyIndex in `computeHighlights`.
  'ply-from': { color: '#7dd3fc', opacity: 0.35, y: 0.03 },
  'ply-to': { color: '#7dd3fc', opacity: 0.65, y: 0.04 },
  // Shields. Grape and berry are the two brand hues the highlight system has
  // not claimed, and they are hex duplicates of `--color-grape` / `--color-berry`
  // in packages/ui/src/theme.css — three.js cannot read CSS custom properties,
  // so the duplication is the established pattern here (see OPP_PLY_COLOR).
  // Threat deliberately avoids the capture red `#ff5555`: red currently means
  // "this move captures", and reusing it would teach a kid that red is two
  // different things.
  //
  // y values are ordered so the always-on markers sit UNDER the history and
  // selection rings (0.028+) — a kid should never have to choose between
  // seeing where a piece is and seeing that it is safe.
  'shield-defended': { color: '#8a63d2', opacity: 0.55, y: 0.022 },
  'shield-threatened': { color: '#e04f7f', opacity: 0.6, y: 0.024 },
  'shield-source': { color: '#ffffff', opacity: 0.85, y: 0.045 },
};

/** Per-ply ring colour. Even plies = opponent (cyan); odd plies = player
 *  counter-reply (mint). The hue gap is wide enough that the player reads
 *  "what I would do" vs "what the engine says they'll do" at a glance. */
const OPP_PLY_COLOR = '#7dd3fc';
const PLAYER_PLY_COLOR = '#a7f3d0';
const PLY_Y_BASE = 0.03;
const PLY_Y_STEP = 0.006;

export function Highlights({
  highlights,
  targets,
  lastMove,
  predictedReply,
}: {
  highlights: readonly Highlight[];
  targets: ReadonlyArray<SquareTarget>;
  lastMove: { from: string; to: string } | null;
  /** Look-ahead: dim last-move tiles when active so the cyan predicted ring
   *  reads as the primary signal and the amber history doesn't compete with it. */
  predictedReply?: ReadonlyArray<{ from: string; to: string }> | null;
}) {
  const rings = useMemo(
    () =>
      highlights
        .filter(
          (h) =>
            h.kind === 'selected' ||
            h.kind === 'movable' ||
            h.kind === 'path' ||
            h.kind === 'check' ||
            h.kind === 'ply-from' ||
            h.kind === 'ply-to' ||
            h.kind === 'shield-defended' ||
            h.kind === 'shield-threatened' ||
            h.kind === 'shield-source',
        )
        .map((h) => ({ h, pos: squareToVec3(h.square) })),
    [highlights],
  );
  const lastSquares = useMemo(
    () =>
      lastMove
        ? [
            { square: lastMove.from, pos: squareToVec3(lastMove.from) },
            { square: lastMove.to, pos: squareToVec3(lastMove.to) },
          ]
        : [],
    [lastMove],
  );

  // When the predicted ring is on the same square as the last-move tile
  // (e.g. last player move was e2→e4 and the robot's reply lands on e4),
  // drop the amber tile to ~30% so the cyan ring reads cleanly. For
  // non-overlapping cases the dim is uniform so the visual signal is
  // consistent: "history fades while you're thinking".
  const lastMoveOpacity = lastMoveOpacityFor(predictedReply);

  return (
    <group>
      {/* Legal-move rings (green valid / red capture). Non-interactive in M0 — the DOM SquareOverlay (Task 21) handles taps. */}
      {targets.map((t) => {
        const kind = t.capture ? 'target-capture' : 'target-valid';
        const spec = KINDS[kind];
        const [x, , z] = squareToVec3(t.square);
        return (
          <mesh
            key={`ring-${t.square}`}
            geometry={ringGeometry}
            position={[x, spec.y, z]}
            rotation={[-Math.PI / 2, 0, 0]}
          >
            <meshBasicMaterial color={spec.color} transparent opacity={spec.opacity} side={THREE.DoubleSide} />
          </mesh>
        );
      })}
      {/* Selection / check / look-ahead rings are non-interactive but visually distinct. */}
      {rings.map(({ h, pos }) => {
        const spec = KINDS[h.kind];
        // `ply-to` rings are the landing squares — they get the larger scale
        // so the player reads them as "where the piece would actually go".
        const isLanding = h.kind === 'ply-to';
        const scale: [number, number, number] = isLanding ? [1.2, 1.2, 1] : [1, 1, 1];
        // Per-ply colour + opacity for the look-ahead chain. Selected /
        // check use the static KINDS entry unchanged.
        const isPly = h.kind === 'ply-from' || h.kind === 'ply-to';
        const plyIndex = h.plyIndex ?? 0;
        const color = isPly ? (plyIndex % 2 === 0 ? OPP_PLY_COLOR : PLAYER_PLY_COLOR) : spec.color;
        // Deeper plies fade slightly so the chain reads in front-to-back
        // depth order without needing z-fighting tricks.
        const opacity = isPly
          ? Math.max(0.3, spec.opacity - plyIndex * 0.1)
          : spec.opacity;
        const y = isPly ? PLY_Y_BASE + plyIndex * PLY_Y_STEP : spec.y;
        // The threat marker is a 4-segment ring, which only reads as a
        // diamond if it is turned 45° in the board plane. The extra Z term
        // is applied after the -PI/2 X rotation that lays the mesh flat, so
        // it spins in-plane rather than tipping the ring out of it.
        const inPlaneSpin = h.kind === 'shield-threatened' ? Math.PI / 4 : 0;
        return (
          <mesh
            key={`${h.kind}-${h.square}-${plyIndex}`}
            geometry={geometryFor(h.kind)}
            position={[pos[0], y, pos[2]]}
            rotation={[-Math.PI / 2, 0, inPlaneSpin]}
            scale={scale}
          >
            <meshBasicMaterial color={color} transparent opacity={opacity} side={THREE.DoubleSide} />
          </mesh>
        );
      })}
      {/* Last-move squares as soft tiles. Dimmed to ~30% opacity while the
          predicted reply is on screen so the cyan ring reads as the primary
          signal (no visual competition between "previous" and "predicted"). */}
      {lastSquares.map(({ square, pos }) => (
        <mesh key={`last-${square}`} geometry={ringGeometry} position={[pos[0], KINDS['last-to'].y, pos[2]]} rotation={[-Math.PI / 2, 0, 0]} scale={[1.4, 1.4, 1] satisfies [number, number, number]}>
          <meshBasicMaterial color={KINDS['last-to'].color} transparent opacity={lastMoveOpacity} side={THREE.DoubleSide} />
        </mesh>
      ))}
    </group>
  );
}
